import React, { useState, useEffect } from "react";

import { Avatar as MuiAvatar, Box, SxProps, Theme } from "@mui/material";
import { ImageOff, User } from "lucide-react";

// WorkOS-synced avatar URLs are generic defaults pulled from the IdP, not
// user-uploaded photos — we prefer initials over them. (VERA-1112)
const IGNORED_AVATAR_HOSTS = ["workoscdn.com"];

/**
 * Derive up-to-two-letter initials from a display name or email.
 * "Spencer Stebbins" -> "SS"; "spencer@vera.inc" -> "S".
 */
export const getInitials = (value?: string | null): string => {
  if (!value) return "";
  // For an email with no spaces, use the local part before the "@".
  const base =
    value.includes("@") && !value.trim().includes(" ")
      ? value.split("@")[0]
      : value;
  return base
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
};

// Only honor an image we'd actually want to show: a non-empty URL that isn't a
// known generic/IdP-default host.
const isHonoredImage = (src?: string | null): boolean =>
  !!src && !IGNORED_AVATAR_HOSTS.some((host) => src.includes(host));

export interface AvatarProps {
  /** Image URL. Ignored when empty or from a generic IdP host (e.g. workoscdn.com) — falls back to initials. */
  src?: string | null;
  /** Display name (or email) used to derive initials when `initials` isn't given. */
  name?: string | null;
  /** Explicit initials override. */
  initials?: string;
  /** Square size in px. */
  size?: number;
  alt?: string;
  sx?: SxProps<Theme>;
}

/**
 * Shared avatar with explicit fallback precedence:
 *   honored image → (onError) broken indicator → initials → person icon.
 *
 * - No image (or an ignored IdP-default URL): shows initials.
 * - Image present but fails to load: shows a broken-image indicator (distinct
 *   from "no image set", per VERA-1112).
 * - No image and no derivable initials: shows a person icon.
 */
export const Avatar = ({
  src,
  name,
  initials,
  size = 40,
  alt,
  sx,
}: AvatarProps) => {
  const [broken, setBroken] = useState(false);
  const honored = isHonoredImage(src);

  // Reset the broken flag whenever the source changes.
  useEffect(() => {
    setBroken(false);
  }, [src]);

  const resolvedInitials = initials ?? getInitials(name ?? alt);
  const showImage = honored && !broken;

  // VRG-231 review item 6 — initials take priority over the "broken image"
  // indicator whenever they're derivable. The distinct ImageOff glyph (VRG-1112)
  // is reserved for the genuinely-unknown case (no name/initials to fall back
  // on) — otherwise a known person with a stale photo URL (e.g. a certificate
  // activity row) rendered a glyph that read as a rendering bug rather than a
  // helpful signal, and diverged from every other avatar in the app (UserBlock's
  // plain MUI Avatar already falls back to initials on a failed image by default).
  const fallback = resolvedInitials ? (
    resolvedInitials
  ) : honored && broken ? (
    // A real image was provided but failed to load, and no initials are
    // derivable either — indicate the broken image rather than a bare person icon.
    <Box
      sx={{ display: "flex", color: "error.main" }}
      title="Image failed to load"
    >
      <ImageOff size={Math.round(size * 0.46)} strokeWidth={2} />
    </Box>
  ) : (
    <User size={Math.round(size * 0.5)} strokeWidth={1.5} />
  );

  return (
    <MuiAvatar
      src={showImage ? (src as string) : undefined}
      alt={alt ?? name ?? undefined}
      imgProps={{ onError: () => setBroken(true) }}
      sx={[
        (theme: Theme) => ({
          width: size,
          height: size,
          fontSize: Math.round(size * 0.36),
          fontWeight: 600,
          backgroundColor:
            theme.palette.primary?.[100] ?? theme.palette.grey[100],
          color: theme.palette.text.secondary,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {fallback}
    </MuiAvatar>
  );
};
